/* istanbul ignore file */
// ut test has been covered by other hook files
/* eslint-disable arrow-body-style */
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useFolders } from './use-folders';
import { useCommonImageRepoMethods } from './use-image-repo-methods';
import { useCommonVideoRepoMethods } from './use-video-repo-methods';
import { useAIGeneratedImages } from './use-ai-generated-images';
import { useFolderPathTree } from './use-folder-path-tree';

const AssetWithFoldersDataServiceContext = React.createContext({});

export const AssetWithFoldersDataServiceProvider = ({
  children, imageRepository, aigcRepository, folderRepository, videoRepository, logger, allowedAssetType, i18n, perfMetricDateRange, defaultPerfMetricDateRange,
}) => {
  const {
    folders,
    folderPath,
    loadFolderData,
    loadMoreFolderData,
    loadFolderDataInDateRange,
    loadFolderArgs,
    loadMoreFolderDataInDateRange,
    perfTimeSeriesData,
    loadPerfTimeSeriesData,
    navigateIntoFolder,
    navigateToFolderInPath,
    navigateIntoFolderItem,
    navigateIntoRootFolder,
    createFolder,
    createFolderBulk,
    deleteFolder,
    renameFolder,
    currentFolder,
    currentFolderId,
    uploadVideoToFolder,
    cancelPendingVideoUpload,
    postVideoByUrl,
    uploadImageByUrlToFolder,
    uploadImageToFolder,
    uploadStockImageToFolder,
    uploadInvestImageToFolder,
    uploadInvestVideoToFolder,
    reloadFolders,
    reloadFoldersInDateRange,
    totalItemCount,
    loadAssetsUnderFolder,
    assets,
    setAssets,
    renameAsset,
    deleteAsset,
    batchDelete,
    batchMove,
    resetFolderData,
    dataOwnerPageName,
    addImageWithBaseIdToFolder,
  } = useFolders({
    repository: folderRepository,
    logger,
    assetType: allowedAssetType,
    i18n,
    perfMetricDateRange,
    defaultPerfMetricDateRange,
  });

  const {
    imageCroppingTasks,
    croppingTaskUpdated,
    getImageById,
    getImageCroppingTaskStatus,
    cropImage,
    addCreative,
  } = useCommonImageRepoMethods({
    imageRepository,
    logger,
    loadedImages: assets,
    setLoadedImages: setAssets,
  });

  const {
    generateImagesByAI,
  } = useAIGeneratedImages({ repository: aigcRepository, logger });

  const {
    getVideoTaskStatus, videoUpdated, addVideoCreative, getVideo,
  } = useCommonVideoRepoMethods({
    repository: folderRepository,
    videoRepository,
    logger,
    i18n,
  });

  const {
    folderPathTree,
    loadFolderPathTreeItem,
    loadMoreFolderPathTreeItem,
    initializeFolderPathTree,
    reloadFolderPathTreeItem,
  } = useFolderPathTree({ repository: folderRepository, defaultPerfMetricDateRange });

  const totalAssetCount =
    totalItemCount.current.ImageAssetsCount + totalItemCount.current.VideoAssetsCount;
  const totalSubfoldersCount = totalItemCount.current.SubFoldersCount;

  const loadDataArgs = useCallback(({
    searchText,
    minImageWidth,
    minImageHeight,
    dateAddedDateRange,
    sortBy,
    importSource,
    assetSources,
    assetsToLoadForSubfolders = 1,
    assetTypeFilter,
    minVideoDuration,
    maxVideoDuration,
    isGlobalSearch = false,
    includeParentHierarchy = false,
    count,
    topRated,
  } = {}) => {
    return loadFolderArgs({
      filterSettings: {
        searchTerm: searchText,
        minWidth: minImageWidth,
        minHeight: minImageHeight,
        dateAddedStart: dateAddedDateRange && dateAddedDateRange.start,
        dateAddedEnd: dateAddedDateRange && dateAddedDateRange.end,
        importSource,
        assetSources,
        assetTypeFilter,
        minVideoDuration,
        maxVideoDuration,
        isGlobalSearch,
        topRated,
      },
      sortBy,
      assetsToLoadForSubfolders,
      includeParentHierarchy,
      count,
    });
  }, [loadFolderArgs]);

  const loadDataImpl = useCallback(({
    useDateRange,
    searchText,
    minImageWidth,
    minImageHeight,
    dateAddedDateRange,
    sortBy,
    importSource,
    assetSources,
    assetsToLoadForSubfolders = 1,
    assetTypeFilter,
    minVideoDuration,
    maxVideoDuration,
    isGlobalSearch = false,
    includeParentHierarchy = false,
    hasPerfData = false,
    includeSubfolders = true,
    count,
    topRated,
    pageName,
    perfMarker,
  } = {}) => {
    const loadFolderDataFunc = useDateRange ? loadFolderDataInDateRange : loadFolderData;
    return loadFolderDataFunc({
      filterSettings: {
        searchTerm: searchText,
        minWidth: minImageWidth,
        minHeight: minImageHeight,
        dateAddedStart: dateAddedDateRange && dateAddedDateRange.start,
        dateAddedEnd: dateAddedDateRange && dateAddedDateRange.end,
        importSource,
        assetSources,
        assetTypeFilter,
        minVideoDuration,
        maxVideoDuration,
        isGlobalSearch,
        hasPerfData,
        topRated,
      },
      perfMarker,
      sortBy,
      assetsToLoadForSubfolders,
      includeParentHierarchy,
      count,
      includeSubfolders,
      pageName,
    });
  }, [loadFolderData, loadFolderDataInDateRange]);

  const loadData = useCallback(args => loadDataImpl({ ...args, useDateRange: false }), [loadDataImpl]);
  const loadDataInDateRange = useCallback(args => loadDataImpl({ ...args, useDateRange: true }), [loadDataImpl]);

  const loadPerfTimeSeriesDataImpl = useCallback((
    perfTimeSeriesParams,
    {
      searchText,
      minImageWidth,
      minImageHeight,
      dateAddedDateRange,
      importSource,
      assetSources,
      // assetTypeFilter,
      minVideoDuration,
      maxVideoDuration,
      isGlobalSearch = false,
      hasPerfData = false,
      topRated,
    } = {}
  ) => {
    return loadPerfTimeSeriesData({
      perfTimeSeriesParams,
      filterSettings: {
        searchTerm: searchText,
        minWidth: minImageWidth,
        minHeight: minImageHeight,
        dateAddedStart: dateAddedDateRange && dateAddedDateRange.start,
        dateAddedEnd: dateAddedDateRange && dateAddedDateRange.end,
        importSource,
        assetSources,
        // assetTypeFilter,
        minVideoDuration,
        maxVideoDuration,
        isGlobalSearch,
        hasPerfData,
        topRated,
      },
    });
  }, [loadPerfTimeSeriesData]);

  const uploadImageByUrl = useCallback(({ imageUrl, folderId, sourceType }) => {
    return uploadImageByUrlToFolder({ imageUrl, folderId, sourceType }).tap(() => {
      logger.logTrace('DataService: image is uploaded by Url to folder', { sourceType, imageUrl, folderId });
    });
  }, [logger, uploadImageByUrlToFolder]);

  const uploadImage = useCallback(({
    imageData,
    updateCurrentProgress = null,
    name = '',
    folderId = null,
    supportUpscale,
  }) => {
    return uploadImageToFolder({
      imageData,
      updateCurrentProgress,
      imageName: name,
      folderId,
      supportUpscale,
    }).tap(() => {
      logger.logTrace('DataService: image is uploaded to folder');
    });
  }, [logger, uploadImageToFolder]);

  const uploadVideo = useCallback(({
    fileObject,
    updateCurrentProgress = null,
    name = '',
    folderId = null,
    perfMarker = null,
  }) => {
    return uploadVideoToFolder({
      fileObject,
      updateCurrentProgress,
      name,
      folderId,
      perfMarker,
    }).tap(() => {
      logger.logTrace('DataService: video is uploaded to folder');
    });
  }, [logger, uploadVideoToFolder]);

  const uploadVideoByUrl = useCallback(({
    videoUrl,
    name = '',
    folderId = null,
  }) => {
    return postVideoByUrl({
      videoUrl,
      name,
      folderId,
    }).tap(() => {
      logger.logTrace('DataService: video is uploaded by Url to folder');
    });
  }, [logger, postVideoByUrl]);

  const uploadStockImage = useCallback(({ stockImageId, folderId }) => {
    return uploadStockImageToFolder({ stockImageId, folderId }).tap(() => {
      logger.logTrace('DataService: stock image is uploaded to folder');
    });
  }, [logger, uploadStockImageToFolder]);

  const uploadInvestImage = useCallback(({ investImageId, imageSrc, folderId }) => {
    return uploadInvestImageToFolder({ investImageId, imageSrc, folderId }).tap(() => {
      logger.logTrace('DataService: invest image is uploaded to folder');
    });
  }, [logger, uploadInvestImageToFolder]);

  const uploadInvestVideo = useCallback(({
    investVideoId, videoSrc, description, folderId,
  }) => {
    return uploadInvestVideoToFolder({
      investVideoId, videoSrc, description, folderId,
    }).tap(() => {
      logger.logTrace('DataService: invest video is uploaded to folder');
    });
  }, [logger, uploadInvestVideoToFolder]);

  const contextVal = useMemo(
    () => ({
      assets,
      totalAssetCount,
      totalSubfoldersCount,
      // load first page, with filters and sortby
      loadData,
      loadDataArgs,
      loadDataInDateRange,
      // reload images with previous filters
      reloadData: reloadFolders,
      reloadDataInDataRange: reloadFoldersInDateRange,
      // load more, with previous filters/sort
      loadMoreData: loadMoreFolderData,
      loadMoreDataInDateRange: loadMoreFolderDataInDateRange,
      getImageById, // if it's not cached, send a request (deep linking)
      perfTimeSeriesData,
      loadPerfTimeSeriesData: loadPerfTimeSeriesDataImpl,

      // get image cropping tasks status
      imageCroppingTasks,
      getImageCroppingTaskStatus,
      croppingTaskUpdated,

      // get video tasks status
      getVideoTaskStatus,
      videoUpdated,
      addVideoCreative,
      getVideo,

      renameAsset,
      deleteAsset,
      cropImage,
      uploadVideo,
      cancelPendingVideoUpload,
      uploadVideoByUrl,
      uploadImage,
      uploadImageByUrl,
      addCreative,
      folders,
      folderPath,
      navigateIntoFolder,
      navigateToFolderInPath,
      navigateIntoFolderItem,
      navigateIntoRootFolder,
      createFolder,
      createFolderBulk,
      deleteFolder,
      renameFolder,
      currentFolder,
      currentFolderId,
      loadAssetsUnderFolder,
      uploadStockImage,
      uploadInvestImage,
      uploadInvestVideo,
      reloadFolders,
      batchDelete,
      batchMove,
      resetFolderData,
      dataOwnerPageName,
      generateImagesByAI,
      addImageWithBaseIdToFolder,

      folderPathTree,
      loadFolderPathTreeItem,
      loadMoreFolderPathTreeItem,
      initializeFolderPathTree,
      reloadFolderPathTreeItem,
    }),
    [assets, totalAssetCount, totalSubfoldersCount, loadData, loadDataArgs, loadDataInDateRange, reloadFolders, reloadFoldersInDateRange, loadMoreFolderData, loadMoreFolderDataInDateRange, getImageById, perfTimeSeriesData, loadPerfTimeSeriesDataImpl, imageCroppingTasks, getImageCroppingTaskStatus, croppingTaskUpdated, getVideoTaskStatus, videoUpdated, addVideoCreative, getVideo, renameAsset, deleteAsset, cropImage, uploadVideo, cancelPendingVideoUpload, uploadVideoByUrl, uploadImage, uploadImageByUrl, addCreative, folders, folderPath, navigateIntoFolder, navigateToFolderInPath, navigateIntoFolderItem, navigateIntoRootFolder, createFolder, createFolderBulk, deleteFolder, renameFolder, currentFolder, currentFolderId, loadAssetsUnderFolder, uploadStockImage, uploadInvestImage, uploadInvestVideo, batchDelete, batchMove, resetFolderData, dataOwnerPageName, generateImagesByAI, folderPathTree, loadFolderPathTreeItem, loadMoreFolderPathTreeItem, initializeFolderPathTree, reloadFolderPathTreeItem, addImageWithBaseIdToFolder]
  );

  return (
    <AssetWithFoldersDataServiceContext.Provider value={contextVal}>
      {children}
    </AssetWithFoldersDataServiceContext.Provider>
  );
};

AssetWithFoldersDataServiceProvider.propTypes = {
  children: PropTypes.node.isRequired,
  imageRepository: PropTypes.shape({
    getImage: PropTypes.func,
    getImageCroppingTaskStatus: PropTypes.func,
    renameImage: PropTypes.func,
    deleteImage: PropTypes.func,
    updateCropSettings: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  aigcRepository: PropTypes.shape({}),
  folderRepository: PropTypes.shape({}).isRequired,
  videoRepository: PropTypes.shape({}).isRequired,
  allowedAssetType: PropTypes.string,
  i18n: PropTypes.shape({}).isRequired,
  perfMetricDateRange: PropTypes.shape({}),
  defaultPerfMetricDateRange: PropTypes.shape({}),
};

AssetWithFoldersDataServiceProvider.defaultProps = {
  aigcRepository: null,
  allowedAssetType: null,
  perfMetricDateRange: null,
  defaultPerfMetricDateRange: null,
};

export const useAsssetWithFoldersDataService = () => useContext(AssetWithFoldersDataServiceContext);
